import { useEffect, useState } from 'react';
import NFTShowcase from './NFTShowcase';
import SfutLegacyShowcase from './SfutLegacyShowcase';
import { prefetchSfutLegacy } from '../web3/sfutLegacyFeed';

const SFUT_FEATURED_COUNT = 5;

function warmSfutLegacy() {
  prefetchSfutLegacy(SFUT_FEATURED_COUNT).catch(() => {
    /* the strip retries when opened */
  });
}

type FeaturedView = 'olympic' | 'sfut';

const TABS: Array<{ id: FeaturedView; label: string; hint: string }> = [
  {
    id: 'olympic',
    label: 'Olympic Series',
    hint: 'Exclusive digital collectibles celebrating athletic excellence',
  },
  {
    id: 'sfut',
    label: 'SFUT Legacy',
    hint: 'Athlete highlights on OpenPlaza',
  },
];

/**
 * One featured gallery at a time. Olympic Series is the default active view.
 * SFUT Legacy mounts only after that tab is selected, but its data and thumbnails are
 * prefetched while the browser is idle (and on tab hover/focus), so it opens instantly.
 */
const FeaturedNftGallery = () => {
  const [view, setView] = useState<FeaturedView>('olympic');
  const active = TABS.find((tab) => tab.id === view) ?? TABS[0];

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(warmSfutLegacy, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const timeout = globalThis.setTimeout(warmSfutLegacy, 1500);
    return () => globalThis.clearTimeout(timeout);
  }, []);

  return (
    <div data-testid="featured-nft-gallery">
      <div className="mb-8 text-center">
        <h2 className="mb-3 text-3xl font-bold text-white">Featured NFTs</h2>
        <p className="mb-6 text-lg text-white/90">{active.hint}</p>
        <div
          role="tablist"
          aria-label="Featured NFT gallery"
          className="inline-flex flex-wrap justify-center gap-2 rounded-xl bg-black/20 p-1"
        >
          {TABS.map((tab) => {
            const selected = tab.id === view;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                data-testid={`featured-tab-${tab.id}`}
                onClick={() => setView(tab.id)}
                onPointerEnter={tab.id === 'sfut' ? warmSfutLegacy : undefined}
                onFocus={tab.id === 'sfut' ? warmSfutLegacy : undefined}
                className={`inline-flex min-h-11 min-w-[9rem] items-center justify-center rounded-lg px-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
                  selected
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'border border-white/20 bg-transparent text-white hover:bg-white/10'
                }`}
              >
                {tab.label}
                {selected ? (
                  <span className="sr-only"> (selected)</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {view === 'olympic' ? (
        <NFTShowcase maxItems={6} compact />
      ) : (
        <SfutLegacyShowcase maxItems={SFUT_FEATURED_COUNT} compact />
      )}
    </div>
  );
};

export default FeaturedNftGallery;
